import React, { useState } from 'react';
import { Clock, ArrowRight, Trash2 } from 'lucide-react';
import { useQuoteDraft } from '../hooks/useQuoteDraft';

interface PendingDraftBannerProps {
  userId?: string;
  onResume: () => void;
  className?: string;
}

export default function PendingDraftBanner({ userId, onResume, className = '' }: PendingDraftBannerProps) {
  const { hasDraft, draftSummary, discardDraft } = useQuoteDraft(userId);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

  if (!hasDraft) return null;

  return (
    <div
      role="region"
      aria-label="Orçamento em andamento"
      className={`bg-amber-500/10 border border-amber-500/30 rounded-2xl p-3 sm:p-3.5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all animate-in fade-in slide-in-from-top-1 duration-200 ${className}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-900 flex items-center justify-center shrink-0 border border-amber-500/25">
          <Clock className="w-4.5 h-4.5" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-black uppercase tracking-wider text-amber-800 block">
            Orçamento em Edição
          </span>
          <p className="text-xs sm:text-sm font-bold text-zinc-900 truncate">
            {draftSummary}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 self-end sm:self-auto shrink-0 w-full sm:w-auto">
        {isConfirmingDiscard ? (
          <div className="flex items-center gap-1.5 w-full sm:w-auto">
            <span className="text-[11px] font-semibold text-zinc-600">Descartar rascunho?</span>
            <button
              type="button"
              onClick={() => {
                discardDraft();
                setIsConfirmingDiscard(false);
              }}
              className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
            >
              Sim
            </button>
            <button
              type="button"
              onClick={() => setIsConfirmingDiscard(false)}
              className="px-2.5 py-1.5 bg-zinc-200 hover:bg-zinc-300 text-zinc-800 text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
            >
              Não
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={onResume}
              className="flex-1 sm:flex-none min-h-[44px] px-4 py-2 bg-amber-500 hover:bg-amber-400 text-neutral-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
            >
              <span>Continuar</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setIsConfirmingDiscard(true)}
              className="min-h-[44px] min-w-[44px] p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors cursor-pointer flex items-center justify-center"
              title="Descartar rascunho"
              aria-label="Descartar rascunho de orçamento"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
